import client from '../client';
import { aiApi } from './ai';
import type { Product, CreateProductInput } from '../../types/product';
import type { ApiResponse, PaginationParams } from '../../types/api';

interface ProductListParams extends PaginationParams {
  search?: string;
  category?: string;
  productType?: string;
  date?: string;
  sort?: string;
  sortDirection?: 'asc' | 'desc';
}

export const productsApi = {
  list: async (storeId: string, params?: ProductListParams) => {
    const res = await client.get<ApiResponse<{ products: Product[] }>>(
      `/dashboard/${storeId}/products`,
      { params },
    );
    const d = res.data.data!;
    return {
      products: d.products || [],
      total: (d as any).total ?? (d.products?.length ?? 0),
      page: (d as any).page ?? 1,
      limit: (d as any).limit ?? 40,
    };
  },

  get: async (storeId: string, productId: string): Promise<Product> => {
    const res = await client.get<ApiResponse<any>>(`/dashboard/${storeId}/products/${productId}`);
    const d = res.data.data!;
    // Backend wraps as { product: {...} } - unwrap if needed
    return d.product || d;
  },

  create: async (storeId: string, data: CreateProductInput) => {
    const res = await client.post<ApiResponse<{ _id: string; productId: string; message: string }>>(`/dashboard/${storeId}/products`, data);
    return res.data.data!;
  },

  update: async (storeId: string, productId: string, data: Partial<CreateProductInput>) => {
    const res = await client.patch<ApiResponse<{ message: string }>>(`/dashboard/${storeId}/products/${productId}`, data);
    return res.data.data!;
  },

  delete: async (storeId: string, productId: string): Promise<void> => {
    await client.delete(`/dashboard/${storeId}/products/${productId}`);
  },

  duplicate: async (storeId: string, productId: string) => {
    const res = await client.post<ApiResponse<{ productId: string; message: string }>>(`/dashboard/${storeId}/products/${productId}/duplicate`);
    return res.data.data!;
  },

  getFilePreviewUrl: (storeId: string, fileName: string): string =>
    `${client.defaults.baseURL}/dashboard/${storeId}/products/files/${encodeURIComponent(fileName)}/preview`,

  generateQuotation: async (
    storeId: string,
    data: {
      items: { productId: string; quantity: number }[];
      customerName?: string;
      validDays?: number;
      notes?: string;
    },
  ) => {
    const res = await client.post(`/dashboard/${storeId}/products/quotation`, data, { responseType: 'blob' });
    return res.data as Blob;
  },

  getUploadPermission: async (storeId: string, productId: string, fileName: string) => {
    const res = await client.post<ApiResponse<{ permissionKey: string; fileName: string; uploadUrl?: string }>>(
      `/dashboard/${storeId}/products/${productId}/upload-permission`,
      { fileName },
    );
    return res.data.data!;
  },

  /**
   * Upload a file-type product's file through the backend (same-origin), which
   * forwards it to the CDN and links it to the product. Avoids browser->CDN CORS.
   * Returns the stored file path. Max ~50MB.
   */
  uploadDigitalFile: async (storeId: string, productId: string, file: File) => {
    const res = await client.post<ApiResponse<{ fileName: string }>>(
      `/dashboard/${storeId}/products/${productId}/upload-file`,
      file,
      // Filename is URL-encoded so non-ASCII names (e.g. Arabic) are header-safe.
      { headers: { 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) } },
    );
    return res.data.data!;
  },

  /**
   * Upload one product image and return the basename to store on the product.
   *
   * Two steps: our API mints a single-use CDN write permission (choosing the
   * destination path itself, so the browser cannot pick where it writes), then
   * the raw file is POSTed to the CDN as multipart form data. Nothing is
   * base64'd and nothing is re-encoded, so the original is stored byte for byte.
   *
   * `credentials: 'omit'` because the CDN authenticates the one-shot permission
   * key alone; sending cookies cross-origin would be pointless and unwanted.
   */
  uploadImage: async (storeId: string, file: File): Promise<string> => {
    const permission = await client.post<ApiResponse<{ permissionKey: string; fileName: string; uploadUrl: string }>>(
      `/dashboard/${storeId}/products/upload-image`,
      { fileName: file.name, size: file.size },
    );
    const { permissionKey, fileName, uploadUrl } = permission.data.data!;

    const form = new FormData();
    form.append('file', file, fileName);

    const res = await fetch(uploadUrl, {
      method: 'POST',
      headers: { authorization: permissionKey },
      body: form,
      credentials: 'omit',
    });
    if (!res.ok) throw new Error(`Image upload failed (${res.status})`);

    return fileName;
  },

  saveSort: async (storeId: string, categoryId: string, products: { id: string; sort: number }[]) => {
    const res = await client.post<ApiResponse<{ matched: number; modified: number }>>(
      `/dashboard/${storeId}/products/sort`,
      { categoryId, products },
    );
    return res.data.data!;
  },

  stats: async (storeId: string, productId: string) => {
    const res = await client.get<ApiResponse<any>>(`/dashboard/${storeId}/products/${productId}/stats`);
    return res.data.data!;
  },

  /** @deprecated Use aiApi.write() from api/endpoints/ai.ts instead */
  aiWrite: (storeId: string, data: { prompt: string; currentContent?: string; productTitle?: string }) =>
    aiApi.write(storeId, data),
};
